import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { TablePagination, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../../utils/tableFunctions'
import EnhancedTableHead from '../../../components/table/EnhancedTableHead'
import TableButtonOpen from '../../../components/table/TableButtonOpen'
import { RouteConstants } from '../../../navigation/navigation-types'
import { ContractorType } from '../../../services/api/sharedTypes/Contractor'
import CollapsibleTableRowForContractors from '../CollapsibleTableRowForContractors'
import PayContractorDialog from '../PayContractorDialog'
import CustomTableCell from '../../../components/common/CustomTableCell'
import { NotPaidContractorDto } from '../../../services/api/sharedTypes/Dashboard'
import { getNumberRoundedInFormattedString } from '../../../utils/global-utils'

export interface NotPaidContractorsRow {
    contractorIdName: string
    contractorName: string
    contractorId: string
    eventIdName: string
    eventId: string
    eventName: string
    clientIdName: string
    clientId: string
    clientName: string
    contractorType: ContractorType
    costs: number
}

const headCells: readonly HeadCell[] = [
    {
        id: 'clientName',
        numeric: false,
        disablePadding: false,
        label: 'Jméno',
    },
    {
        id: 'eventName',
        numeric: false,
        disablePadding: false,
        label: 'Událost',
    },
    {
        id: 'contractorType',
        numeric: false,
        disablePadding: false,
        label: 'Faktruace',
    },
    {
        id: 'contractorName',
        numeric: false,
        disablePadding: false,
        label: 'Jméno',
    },
    {
        id: 'costs',
        numeric: false,
        disablePadding: false,
        label: 'Zaplatit',
        align: 'right',
    },
    {
        id: 'pay',
        numeric: false,
        disablePadding: false,
        label: 'Suma',
        align: 'right',
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

interface NotPaidContractorsTableProps {
    notPaidContractors: NotPaidContractorDto[]
    payContractor: (contractorId: string, eventId: string) => void
}

const NotPaidContractorsTable: React.FC<NotPaidContractorsTableProps> = ({
    notPaidContractors,
    payContractor,
}: NotPaidContractorsTableProps) => {
    const [order, setOrder] = React.useState<Order>('asc')
    const [orderBy, setOrderBy] = React.useState<string>('contractorName')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    /**
     * getSortedAndGroupedNotPaidContractors is used to sort and group notPaidContractors by contractorIdName.id
     * @param arr - notPaidContractors array typeof NotPaidContractorDto[]
     * @returns {NotPaidContractorsRow[]} sorted and grouped notPaidContractors
     */
    const getSortedAndGroupedNotPaidContractors = (arr: NotPaidContractorDto[]) => {
        const sortedArr: NotPaidContractorsRow[] = stableSort(
            arr.map((row) => {
                return {
                    ...row,
                    contractorIdName: `${row.contractorIdName.id} - ${row.contractorIdName.name}`,
                    contractorName: row.contractorIdName.name,
                    contractorId: row.contractorIdName.id ?? '',
                    eventIdName: `${row.eventIdName.id} - ${row.eventIdName.name}`,
                    eventId: row.eventIdName.id ?? '',
                    eventName: row.eventIdName.name,
                    clientIdName: `${row.clientIdName.id} - ${row.clientIdName.name}`,
                    clientId: row.clientIdName.id ?? '',
                    clientName: row.clientIdName.name,
                }
            }),
            getComparator(order, orderBy)
        )

        return sortedArr.reduce<{
            contractorId: NotPaidContractorsRow[]
        }>(
            function (rv, x) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                // eslint-disable-next-line @typescript-eslint/no-extra-semi
                ; (rv[x['contractorId']] = rv[x['contractorId']] || []).push(x)
                return rv
            },
            { contractorId: [] }
        )
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                {notPaidContractors.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                            rowCount={notPaidContractors.length}
                        />
                        <TableBody>
                            {Object.values(getSortedAndGroupedNotPaidContractors(notPaidContractors))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index: number) => {
                                    if (row.length === 0) return null
                                    if (row.length <= 1)
                                        return (
                                            <TableRow hover tabIndex={-1} key={index}>
                                                <CustomTableCell
                                                    isText
                                                    maxWidth={350}
                                                    style={{ cursor: 'pointer' }}
                                                    route={RouteConstants.events + '/' + row[0].eventId}
                                                >
                                                    <div style={{ paddingLeft: 50 }} >
                                                        {row[0].clientName}
                                                    </div>
                                                </CustomTableCell>
                                                <CustomTableCell route={RouteConstants.clients + '/' + row[0].clientId} isText>
                                                    {row[0].eventName}
                                                </CustomTableCell>
                                                <CustomTableCell
                                                    isText
                                                    style={{ cursor: 'pointer' }}
                                                    route={RouteConstants.events + '/' + row[0].eventId}
                                                >
                                                    {row[0].contractorType === ContractorType.Brigadier
                                                        ? 'Brigádník'
                                                        : 'Dopravce'}
                                                </CustomTableCell>
                                                <CustomTableCell
                                                    isText
                                                    route={RouteConstants.workersAndCarriers + '/' + row[0].contractorId}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {row[0].contractorName}
                                                </CustomTableCell>
                                                <CustomTableCell
                                                    align='right'
                                                    style={{ cursor: 'pointer' }}
                                                    route={RouteConstants.events + '/' + row[0].eventId}
                                                >
                                                    {getNumberRoundedInFormattedString(row[0].costs)}
                                                </CustomTableCell>
                                                <CustomTableCell
                                                    align='right'
                                                    style={{ cursor: 'pointer' }}
                                                    route={RouteConstants.events + '/' + row[0].eventId}
                                                ></CustomTableCell>
                                                <CustomTableCell align='right'>
                                                    <PayContractorDialog
                                                        onContractorPaid={() =>
                                                            payContractor(row[0].contractorId, row[0].eventId)
                                                        }
                                                    />
                                                    <TableButtonOpen route={RouteConstants.events} id={row[0].eventId} />
                                                </CustomTableCell>
                                            </TableRow>
                                        )
                                    if (row.length > 1)
                                        return (
                                            <CollapsibleTableRowForContractors
                                                key={index}
                                                payContractor={payContractor}
                                                tableRowValues={row}
                                            />
                                        )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component='div'
                    count={notPaidContractors.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    )
}

export default NotPaidContractorsTable
